import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  CloudIcon, 
  DevicePhoneMobileIcon,
  ServerIcon,
  CodeBracketIcon,
  ChartBarIcon,
  StarIcon,
  CheckCircleIcon,
  PlayIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import heroBg from '../assets/hero-bg.svg';
import cybersecurityImage from '../assets/Cybersecurity & Compliance Design.png';
import techImage1 from '../assets/ChatGPT Image Aug 23, 2025, 02_54_12 PM.png';
import techImage2 from '../assets/ChatGPT Image Aug 23, 2025, 02_57_13 PM.png';
import techImage3 from '../assets/ChatGPT Image Aug 23, 2025, 02_58_33 PM.png';
import techImage4 from '../assets/ChatGPT Image Aug 23, 2025, 03_15_40 PM.png';

const Home: React.FC = () => {
  // Helper function to get service features
  const getServiceFeatures = (title: string) => {
    const featuresMap: { [key: string]: string[] } = {
      'Application & Software Development': [
        'Custom web applications',
        'Desktop software solutions',
        'Cross-platform compatibility',
        'Database integration',
        'Third-party API integration'
      ],
      'Mobile App Development': [
        'Native iOS & Android apps',
        'Cross-platform development',
        'App Store deployment',
        'Push notifications',
        'Offline functionality'
      ],
      'AI Solutions': [
        'Intelligent chatbots',
        'Machine learning models',
        'Data analytics dashboards',
        'Automated workflows',
        'Predictive analytics'
      ],
      'API Integration & DevOps': [
        'RESTful API development',
        'Microservices architecture',
        'CI/CD pipeline setup',
        'Cloud deployment',
        'Monitoring & logging'
      ],
      'UI/UX & Graphic Design': [
        'User experience design',
        'Brand identity creation',
        'Marketing materials',
        'Interactive prototypes',
        'Responsive design'
      ],
      'IT Strategy & Support': [
        'Technology roadmapping',
        'System architecture planning',
        '24/7 technical support',
        'Performance optimization',
        'Security audits'
      ]
    };
    return featuresMap[title] || [];
  };

  const services = [
    {
      icon: CodeBracketIcon,
      title: 'Application & Software Development',
      description: 'Web, Mobile (iOS/Android), Desktop, PWAs - Cross-platform solutions',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Mobile App Development',
      description: 'Native & Cross-platform apps for iOS and Android with Play/App Store deployment',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: ChartBarIcon,
      title: 'AI Solutions',
      description: 'AI Chatbots, Power BI Dashboards, Machine Learning integrations',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: ServerIcon,
      title: 'API Integration & DevOps',
      description: 'API development, integration, DevOps automation, CI/CD pipelines',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      icon: ShieldCheckIcon,
      title: 'UI/UX & Graphic Design',
      description: 'User experience design, graphics, logos, audio/video content, marketing templates',
      color: 'bg-red-50 text-red-600'
    },
    {
      icon: CloudIcon,
      title: 'IT Strategy & Support',
      description: 'Digital roadmapping, QA, Day-2 operations, ongoing support contracts',
      color: 'bg-indigo-50 text-indigo-600'
    }
  ];

  const trustSignals = [
    { name: 'iOS', logo: 'iOS' },
    { name: 'Android', logo: 'AND' },
    { name: 'React', logo: 'REACT' },
    { name: 'Node.js', logo: 'NODE' },
    { name: 'Python', logo: 'PY' },
    { name: 'AI/ML', logo: 'AI' }
  ];

  const testimonials = [
    {
      quote: "Cybtex built our patient portal app in just 6 weeks. The quality exceeded expectations, and their support team has been amazing. Worth every penny!",
      author: "Dr. Sarah Johnson",
      company: "Healthcare Clinic Director",
      rating: 5
    },
    {
      quote: "We needed a custom real estate CRM and mobile app. Cybtex delivered on time, on budget, with a money-back guarantee that gave us confidence. Highly recommend!",
      author: "Michael Chen",
      company: "Real Estate Broker",
      rating: 5
    }
  ];

  const whyChooseUs = [
    { title: 'Money-Back Guarantee', description: 'We stand behind our work with a 100% satisfaction guarantee' },
    { title: 'Affordable & Transparent', description: 'Cost-effective solutions with no hidden fees' },
    { title: 'Domestic 24/7 Support', description: 'Real humans, real-time help whenever you need it' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Cybersecurity Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black opacity-50"></div>
          
          {/* Background text overlay */}
          <div className="absolute top-20 right-20 text-white/20 text-8xl font-bold rotate-12">
            SECURE
          </div>
          <div className="absolute bottom-20 right-32 text-white/20 text-6xl font-bold -rotate-12">
            MODERN
          </div>
        </div>
        <div className="relative container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                Build. Launch. Grow.
                <span className="block text-primary-200">Your Trusted Software Development Partner.</span>
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                Cybtex Solutions delivers custom software development from web and mobile apps to AI solutions. 
                Affordable, rapid delivery with money-back guarantee and ongoing support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
                  Start Your Project
                </Link>
                <Link to="/services" className="btn-secondary bg-transparent border-2 border-white text-cyan-300 hover:bg-cyan-500 hover:text-white">
                  <PlayIcon className="w-5 h-5 mr-2 inline" />
                  View Our Services
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-white/20 to-accent-500/20 rounded-lg p-4 text-center border border-white/30">
                    <div className="text-3xl font-bold">20+</div>
                    <div className="text-sm">Years Experience</div>
                  </div>
                  <div className="bg-gradient-to-br from-white/20 to-accent-500/20 rounded-lg p-4 text-center border border-white/30">
                    <div className="text-3xl font-bold">200+</div>
                    <div className="text-sm">Apps Delivered</div>
                  </div>
                  <div className="bg-gradient-to-br from-white/20 to-accent-500/20 rounded-lg p-4 text-center border border-white/30">
                    <div className="text-3xl font-bold">100%</div>
                    <div className="text-sm">Money-Back Guarantee</div>
                  </div>
                  <div className="bg-gradient-to-br from-white/20 to-accent-500/20 rounded-lg p-4 text-center border border-white/30">
                    <div className="text-3xl font-bold">24/7</div>
                    <div className="text-sm">Domestic Support</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About & Trust Signals */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Trusted by Healthcare, Education & Small Businesses
              </h2>
              <p className="text-lg text-secondary-600 mb-8 leading-relaxed">
                With 20+ years of software development experience, Cybtex Solutions specializes in custom applications for healthcare providers, schools, real estate agencies, and Maryland-based startups. We deliver affordable, high-quality solutions with rapid deployment and ongoing support.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {trustSignals.map((signal) => (
                  <div key={signal.name} className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-4 text-center border border-primary-100 shadow-sm">
                    <div className="font-semibold text-secondary-800">{signal.logo}</div>
                    <div className="text-sm text-secondary-600">{signal.name}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={cybersecurityImage} 
                alt="Cybersecurity & Compliance" 
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent rounded-2xl"></div>
              
              {/* Top overlay text */}
              <div className="absolute top-6 left-6 text-white">
                <div className="bg-primary-600/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <p className="text-sm font-semibold">💻 Custom Development</p>
                </div>
              </div>
              
              {/* Center overlay text */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-white">
                <div className="bg-black/60 backdrop-blur-sm px-6 py-4 rounded-xl">
                  <h3 className="text-2xl font-bold mb-2">Why Choose Cybtex?</h3>
                  <p className="text-primary-200 text-sm mb-4">Affordable • Rapid • Guaranteed</p>
                </div>
              </div>
              
              {/* Bottom content */}
              <div className="absolute bottom-6 left-6 text-white">
                <div className="space-y-3">
                  {whyChooseUs.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <CheckCircleIcon className="w-5 h-5 text-primary-200 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white">{item.title}</h4>
                        <p className="text-primary-100 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="section-padding bg-gradient-to-br from-primary-50/30 to-accent-50/30">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Our Development Services
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              From web and mobile apps to AI solutions - we build custom software that drives your business forward.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-secondary-100"
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${service.color} mb-6`}
                >
                  <service.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-secondary-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <div className="space-y-3 mb-6">
                  {getServiceFeatures(service.title).map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-secondary-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/services"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  Learn More
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white relative">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={techImage1} 
            alt="Background Pattern" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Trusted by healthcare providers, schools, real estate agencies, and small businesses across Maryland
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-secondary-50 rounded-xl p-8"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-lg text-secondary-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-secondary-900">{testimonial.author}</div>
                    <div className="text-secondary-600">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Build Your Custom Software?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Get a free consultation and quote. With our money-back guarantee, you have nothing to lose!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
              Start Your Project
            </Link>
            <Link to="/services" className="btn-secondary bg-transparent border-2 border-white text-cyan-300 hover:bg-cyan-500 hover:text-white">
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
